import React, { useState } from 'react';
import classNames from 'classnames';

interface ButtonProps {
  animate?: boolean;
  href?: string;
  flat?: boolean;
  outline?: boolean;
  bezel?: boolean;
  pill?: boolean;
  primary?: boolean;
  secondary?: boolean;
  transparent?: boolean;
  disabled?: boolean;
  disposable?: boolean;
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onTouchStart?: React.TouchEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  onTouchEnd?: React.TouchEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  children: React.ReactNode;
}

export function Button({
  animate,
  href,
  flat,
  outline,
  bezel,
  pill,
  primary,
  secondary,
  transparent,
  disabled,
  disposable,
  onClick,
  children,
  ...rest
}: ButtonProps) {
  const [rippleAnimationStyle, setRippleAnimationStyle] = useState({});
  const [ripple, setRipple] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    const buttonElement = event.currentTarget;
    const buttonElementRec = buttonElement.getBoundingClientRect();

    const x = event.pageX - buttonElementRec.left - window.scrollX;
    const y = event.pageY - buttonElementRec.top - window.scrollY;

    const diameter = Math.max(
      buttonElement.clientWidth,
      buttonElement.clientHeight
    );

    // If the button is not transparent, show the ripple effect
    if (!transparent) {
      setRippleAnimationStyle({
        top: `${y - diameter / 2}px`,
        left: `${x - diameter / 2}px`,
        width: `${diameter}px`,
        height: `${diameter}px`
      });
      setRipple(true);

      setTimeout(() => {
        setRippleAnimationStyle({});
        setRipple(false);
      }, 500);
    }

    if (onClick) {
      onClick(event);
    }

    setIsButtonClicked(false);
  };
  const buttonAnimationStyle = animate
    ? {
        transform: animate && isButtonClicked ? 'scale(0.96)' : 'scale(1)', // Apply styles based on button click status
        transition: 'transform 0.3s'
      }
    : {};

  const buttonTypeClass = classNames({
    'betterMood__button-flat': flat,
    'betterMood__button-outline': outline,
    'betterMood__button-bezel': bezel
  });

  // Determine the state of the button
  const buttonStateClass = classNames({
    'betterMood__button-primary': primary,
    'betterMood__button-secondary': secondary,
    'betterMood__button-transparent': transparent,
    'betterMood__button-pill': pill,
    'betterMood__button-disabled': disabled,
    'betterMood__button-disposable': disposable
  });

  const classname = classNames(buttonTypeClass, buttonStateClass);

  if (!href) {
    return (
      <button
        type="button"
        className={classname}
        disabled={disabled}
        onClick={handleClick}
        onPointerDown={() => setIsButtonClicked(true)} // Update state on Pointer down
        onPointerUp={() => setIsButtonClicked(false)} // Update state on Pointer up
        onPointerLeave={() => setIsButtonClicked(false)} // Update state on Pointer leave
        style={buttonAnimationStyle}
        {...rest}>
        {children}
        <span
          className={`betterMood__ripple ${
            ripple ? 'betterMood__ripple-active' : ''
          }`}
          style={rippleAnimationStyle}
        />
      </button>
    );
  } else {
    return (
      <a
        className={classname}
        onClick={handleClick}
        style={buttonAnimationStyle}
        {...rest}
        href={href}>
        {children}
        <span
          className={`betterMood__ripple ${
            ripple ? 'betterMood__ripple-active' : ''
          }`}
          style={rippleAnimationStyle}
        />
      </a>
    );
  }
}
